import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { FiDownload } from 'react-icons/fi';

interface StatProps {
  value: number;
  suffix: string;
  label: string;
  delay?: number;
}

const AnimatedStat = ({ value, suffix, label, delay = 0 }: StatProps) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const controls = animate(count, value, {
            duration: 2,
            delay,
          });
          return () => controls.stop();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [count, value, delay]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="text-center"
    >
      <div className="text-4xl md:text-5xl font-bold gradient-text mb-2">
        <motion.span>{rounded}</motion.span>
        {suffix}
      </div>
      <div className="text-gray-600 dark:text-dark-400 font-medium">{label}</div>
    </motion.div>
  );
};

export const About = () => {
  return (
    <section id="about" className="py-20 relative overflow-hidden">
      {/* Background gradient effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-500/10 rounded-full blur-3xl" />
      </div>

      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            About <span className="gradient-text">Us</span>
          </h2>

          {/* Animated underline */}
          <motion.div
            className="h-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mx-auto mb-16"
            initial={{ width: 0 }}
            whileInView={{ width: '100px' }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Profile Photo Placeholder */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative group">
              {/* Gradient border */}
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition duration-500" />

              {/* Company Logo */}
              <div className="relative bg-gray-100 dark:bg-dark-800 rounded-2xl overflow-hidden aspect-square border border-gray-200 dark:border-dark-700 flex items-center justify-center">
                <div className="text-9xl font-bold bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 text-transparent bg-clip-text">
                  TS
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary-500/20 rounded-full blur-2xl" />
            </div>
          </motion.div>

          {/* About Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Glass card with gradient border */}
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl opacity-30 group-hover:opacity-50 transition duration-500 blur" />
              <div className="relative bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-dark-700">
                <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-dark-50">
                  Elite Technology Solutions Provider
                </h3>
                <div className="space-y-4 text-gray-600 dark:text-dark-300 leading-relaxed">
                  <p>
                    <span className="gradient-text font-semibold">Tech Sierra Solutions</span> is a premier technology
                    solutions company specializing in AI-powered applications, full-stack architecture, and cloud
                    infrastructure. We deliver cutting-edge solutions that transform businesses and drive innovation.
                  </p>
                  <p>
                    With expertise spanning modern web technologies, machine learning, and enterprise-scale cloud
                    deployments, we architect robust, scalable systems that meet the demands of today's digital
                    landscape. Our portfolio includes AI analytics platforms, event management systems, and
                    enterprise infrastructure solutions.
                  </p>
                  <p>
                    What sets us apart is our commitment to excellence and our ability to deliver production-ready
                    solutions using the latest technologies. From React and Next.js to AI integration and cloud
                    orchestration, we build systems that scale and perform.
                  </p>
                </div>

                {/* Contact Us Button */}
                <motion.a
                  href="#contact"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mt-6 btn-primary inline-flex items-center gap-2"
                >
                  <FiDownload className="text-xl" />
                  <span>Get In Touch</span>
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20"
        >
          {/* Glass card for stats */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 rounded-2xl opacity-20 group-hover:opacity-40 transition duration-500 blur" />
            <div className="relative bg-white/60 dark:bg-dark-800/60 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-gray-200 dark:border-dark-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                <AnimatedStat
                  value={10}
                  suffix="+"
                  label="Years Experience"
                  delay={0.2}
                />
                <AnimatedStat
                  value={70}
                  suffix="+"
                  label="Projects Delivered"
                  delay={0.4}
                />
                <AnimatedStat
                  value={27}
                  suffix="+"
                  label="Active Domains"
                  delay={0.6}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
